import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { surfaceLabel as pickerLabel } from 'src/components/livy/LivySurfacePicker';
import { Box, Typography } from '@mui/material';
import { Area, AreaChart, Bar, BarChart, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from 'src/api';
import { useChartTheme } from 'src/components/livy/LivyChart';
const EMPTY_DAY = {
  costUsd: 0,
  turns: 0,
  errors: 0,
  input: 0,
  output: 0,
  cacheRead: 0,
  cacheWrite: 0
};

/**
 * Every UTC day in the window, zero-filled, so a quiet day reads as a gap rather than vanishing. An
 * older proxy sends only byDay costs; those still chart, and the page hides what it cannot know.
 */
export function dailySeries(daily, from, to) {
  let byDay = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  const out = [];
  const end = to.slice(0, 10);
  for (let d = new Date("".concat(from.slice(0, 10), "T00:00:00Z"));; d.setUTCDate(d.getUTCDate() + 1)) {
    var _byDay$day;
    const day = d.toISOString().slice(0, 10);
    out.push(_objectSpread(_objectSpread({
      day
    }, EMPTY_DAY), {}, {
      costUsd: (_byDay$day = byDay[day]) !== null && _byDay$day !== void 0 ? _byDay$day : 0
    }, daily === null || daily === void 0 ? void 0 : daily[day]));
    if (day >= end || out.length > 400) return out;
  }
}
const money = n => "$".concat(n < 1 ? n.toFixed(4) : n.toFixed(2));
const axisMoney = n => "$".concat(n.toFixed(2));
const compact = n => Intl.NumberFormat('en', {
  notation: 'compact'
}).format(n);
const shortDay = day => day.slice(5);
export const ChartCard = _ref => {
  let title = _ref.title,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
      p: '12px 14px 6px',
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11,
      letterSpacing: '.07em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '8px'
    }
  }, title), children);
};
function useAxes() {
  const _useChartTheme = useChartTheme(),
    series = _useChartTheme.series,
    axis = _useChartTheme.axis,
    grid = _useChartTheme.grid,
    tooltipProps = _useChartTheme.tooltipProps;
  const axisProps = {
    tick: {
      fill: axis,
      fontSize: 11
    },
    tickLine: false,
    axisLine: {
      stroke: grid
    }
  };
  return {
    series,
    axisProps,
    tooltipProps,
    legend: {
      wrapperStyle: {
        fontSize: 11,
        color: axis
      }
    }
  };
}
export function DailyCostChart(_ref2) {
  let data = _ref2.data,
    _ref2$costOnly = _ref2.costOnly,
    costOnly = _ref2$costOnly === void 0 ? false : _ref2$costOnly;
  const _useAxes = useAxes(),
    series = _useAxes.series,
    axisProps = _useAxes.axisProps,
    tooltipProps = _useAxes.tooltipProps,
    legend = _useAxes.legend;
  return /*#__PURE__*/React.createElement(ChartCard, {
    title: costOnly ? 'Cost by day (UTC)' : 'Cost and turns by day (UTC)'
  }, /*#__PURE__*/React.createElement(ResponsiveContainer, {
    width: "100%",
    height: 220
  }, /*#__PURE__*/React.createElement(ComposedChart, {
    data: data,
    margin: {
      top: 4,
      right: 4,
      bottom: 0,
      left: 0
    }
  }, /*#__PURE__*/React.createElement(XAxis, _extends({
    dataKey: "day",
    tickFormatter: shortDay,
    minTickGap: 16
  }, axisProps)), /*#__PURE__*/React.createElement(YAxis, _extends({
    yAxisId: "cost",
    tickFormatter: axisMoney,
    width: 52
  }, axisProps)), /*#__PURE__*/React.createElement(YAxis, _extends({
    yAxisId: "turns",
    orientation: "right",
    allowDecimals: false,
    width: 32
  }, axisProps)), /*#__PURE__*/React.createElement(Tooltip, _extends({}, tooltipProps, {
    formatter: (v, name) => [name === 'Cost' ? money(v) : v.toLocaleString(), name]
  })), /*#__PURE__*/React.createElement(Legend, legend), /*#__PURE__*/React.createElement(Bar, {
    yAxisId: "cost",
    dataKey: "costUsd",
    name: "Cost",
    fill: series[0],
    radius: [3, 3, 0, 0]
  }), !costOnly && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Line, {
    yAxisId: "turns",
    dataKey: "turns",
    name: "Turns",
    stroke: series[1],
    dot: false,
    strokeWidth: 2
  }), /*#__PURE__*/React.createElement(Line, {
    yAxisId: "turns",
    dataKey: "errors",
    name: "Errors",
    stroke: series[4],
    dot: false,
    strokeWidth: 1.5
  })))));
}
const TOKEN_KINDS = [['cacheRead', 'Cache read (0.1x)'], ['cacheWrite', 'Cache write (2x)'], ['input', 'Uncached input (1x)'], ['output', 'Output']];
export function DailyTokensChart(_ref3) {
  let data = _ref3.data,
    note = _ref3.note;
  const _useAxes2 = useAxes(),
    series = _useAxes2.series,
    axisProps = _useAxes2.axisProps,
    tooltipProps = _useAxes2.tooltipProps,
    legend = _useAxes2.legend;
  return /*#__PURE__*/React.createElement(ChartCard, {
    title: "Tokens by day (UTC)"
  }, /*#__PURE__*/React.createElement(ResponsiveContainer, {
    width: "100%",
    height: 220
  }, /*#__PURE__*/React.createElement(AreaChart, {
    data: data,
    margin: {
      top: 4,
      right: 4,
      bottom: 0,
      left: 0
    }
  }, /*#__PURE__*/React.createElement(XAxis, _extends({
    dataKey: "day",
    tickFormatter: shortDay,
    minTickGap: 16
  }, axisProps)), /*#__PURE__*/React.createElement(YAxis, _extends({
    tickFormatter: compact,
    width: 44
  }, axisProps)), /*#__PURE__*/React.createElement(Tooltip, _extends({}, tooltipProps, {
    formatter: (v, name) => [v.toLocaleString(), name]
  })), /*#__PURE__*/React.createElement(Legend, legend), TOKEN_KINDS.map((_ref4, i) => {
    let _ref5 = _slicedToArray(_ref4, 2),
      key = _ref5[0],
      name = _ref5[1];
    return /*#__PURE__*/React.createElement(Area, {
      key: key,
      dataKey: key,
      name: name,
      stackId: "t",
      stroke: series[i],
      fill: series[i],
      fillOpacity: 0.35
    });
  }))), note && /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    component: "p",
    sx: {
      mb: '6px'
    }
  }, note));
}

/** A ranked horizontal bar per key: the outlier is visible before anything is read. */
export function BreakdownChart(_ref6) {
  let title = _ref6.title,
    data = _ref6.data,
    _ref6$format = _ref6.format,
    format = _ref6$format === void 0 ? money : _ref6$format;
  const _useAxes3 = useAxes(),
    series = _useAxes3.series,
    axisProps = _useAxes3.axisProps,
    tooltipProps = _useAxes3.tooltipProps;
  const rows = data.slice(0, 8).map(_ref7 => {
    let _ref8 = _slicedToArray(_ref7, 2),
      name = _ref8[0],
      value = _ref8[1];
    return {
      name,
      value
    };
  });
  return /*#__PURE__*/React.createElement(ChartCard, {
    title: title
  }, rows.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    component: "p",
    sx: {
      pb: '8px'
    }
  }, "Nothing yet") : /*#__PURE__*/React.createElement(ResponsiveContainer, {
    width: "100%",
    height: Math.max(80, rows.length * 28 + 16)
  }, /*#__PURE__*/React.createElement(BarChart, {
    data: rows,
    layout: "vertical",
    margin: {
      top: 0,
      right: 12,
      bottom: 0,
      left: 0
    }
  }, /*#__PURE__*/React.createElement(XAxis, {
    type: "number",
    hide: true
  }), /*#__PURE__*/React.createElement(YAxis, _extends({
    type: "category",
    dataKey: "name",
    width: 150,
    tickFormatter: s => s.length > 22 ? "".concat(s.slice(0, 21), "\u2026") : s
  }, axisProps)), /*#__PURE__*/React.createElement(Tooltip, _extends({}, tooltipProps, {
    formatter: v => [format(v), title.split(' by ')[0]]
  })), /*#__PURE__*/React.createElement(Bar, {
    dataKey: "value",
    fill: series[0],
    radius: [0, 3, 3, 0],
    barSize: 14
  }))));
}
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PAGE = 500;
let directory = null;
/** Every company's name from the admin list the company switcher reads, fetched once per page load. */
const loadDirectory = async () => {
  const names = {};
  for (let page = 1;; page += 1) {
    var _res$items2, _res$totalCount;
    const res = await api.AdminApi.getCompaniesList({
      page,
      pageSize: PAGE,
      search: ''
    }, {});
    for (const c of (_res$items = res === null || res === void 0 ? void 0 : res.items) !== null && _res$items !== void 0 ? _res$items : []) {
      var _res$items;
      if (c.companyId && c.companyName) names[c.companyId.toLowerCase()] = c.companyName;
    }
    if (!(res !== null && res !== void 0 && (_res$items2 = res.items) !== null && _res$items2 !== void 0 && _res$items2.length) || page * PAGE >= ((_res$totalCount = res.totalCount) !== null && _res$totalCount !== void 0 ? _res$totalCount : 0)) return names;
  }
};

/** Company ids to names. An id the directory lacks keeps a short form rather than a blank. */
export function useCompanyNames(enabled) {
  const _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    names = _useState2[0],
    setNames = _useState2[1];
  useEffect(() => {
    // The directory is staff data: never fetched for someone the page is about to turn away.
    if (!enabled) return undefined;
    let live = true;
    directory !== null && directory !== void 0 ? directory : directory = loadDirectory().catch(() => {
      directory = null;
      return {};
    });
    void directory.then(n => live && setNames(n));
    return () => {
      live = false;
    };
  }, [enabled]);
  return id => {
    var _names$id$toLowerCase;
    return id && GUID.test(id) ? (_names$id$toLowerCase = names[id.toLowerCase()]) !== null && _names$id$toLowerCase !== void 0 ? _names$id$toLowerCase : "".concat(id.slice(0, 8), "\u2026") : 'No company';
  };
}

/** A Cognito sub is all a customer row carries (no email on the long-retention tier), so say what it is. */
export const personLabel = (who, surface) => !who ? '—' : GUID.test(who) ? "".concat(surface === 'pm' ? 'Customer' : 'User', " ").concat(who.slice(0, 8)) : who;

/** A staff member on the pm surface is a View as PM preview, not customer usage; customer rows carry no email. */
export const surfaceLabel = function () {
  let surface = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'admin';
  let who = arguments.length > 1 ? arguments[1] : undefined;
  return "".concat(pickerLabel(surface)).concat(surface === 'pm' && who && !GUID.test(who) ? ' · preview' : '');
};